export type QueryKeyPart = string | number | boolean | null | undefined;
export type QueryKeyFilters = Record<string, QueryKeyPart>;

export const queryKeys = {
  seller: {
    root: (storeSlug: string) => ["seller", storeSlug] as const,
    session: (storeSlug: string) => ["seller", storeSlug, "session"] as const,
    profile: (storeSlug: string) => ["seller", storeSlug, "profile"] as const,
    store: (storeSlug: string) => ["seller", storeSlug, "store"] as const,
    settings: (storeSlug: string) => ["seller", storeSlug, "settings"] as const,
    storeSettings: (storeSlug: string) =>
      ["seller", storeSlug, "store-settings"] as const,
    storeGovernorates: (storeSlug: string) =>
      ["seller", storeSlug, "governorates"] as const,
    customDomain: (storeSlug: string) =>
      ["seller", storeSlug, "custom-domain"] as const,
    shippingRulesPrefix: (storeSlug: string) =>
      ["seller", storeSlug, "shipping-rules"] as const,
    shippingRules: (storeSlug: string) =>
      ["seller", storeSlug, "shipping-rules", "list"] as const,
    freeShippingRulesPrefix: (storeSlug: string) =>
      ["seller", storeSlug, "free-shipping-rules"] as const,
    freeShippingRules: (storeSlug: string) =>
      ["seller", storeSlug, "free-shipping-rules", "list"] as const,
    storeSubscription: (storeSlug: string) =>
      ["seller", storeSlug, "store-subscription"] as const,
    categoriesPrefix: (storeSlug: string) =>
      ["seller", storeSlug, "categories"] as const,
    categories: (storeSlug: string, filters?: QueryKeyFilters) =>
      ["seller", storeSlug, "categories", "list", filters ?? {}] as const,
    productsPrefix: (storeSlug: string) =>
      ["seller", storeSlug, "products"] as const,
    products: (storeSlug: string, filters?: QueryKeyFilters) =>
      ["seller", storeSlug, "products", "list", filters ?? {}] as const,
    productById: (storeSlug: string, productId: string) =>
      ["seller", storeSlug, "products", productId] as const,
    productVariantCosts: (storeSlug: string, productId: string) =>
      ["seller", storeSlug, "products", productId, "variant-costs"] as const,
    ordersPrefix: (storeSlug: string) =>
      ["seller", storeSlug, "orders"] as const,
    orders: (storeSlug: string, filters?: QueryKeyFilters) =>
      ["seller", storeSlug, "orders", "list", filters ?? {}] as const,
    orderStatusCounts: (storeSlug: string, filters?: QueryKeyFilters) =>
      filters
        ? (["seller", storeSlug, "orders", "status-counts", filters] as const)
        : (["seller", storeSlug, "orders", "status-counts"] as const),
    ordersSmart: (storeSlug: string, filters?: QueryKeyFilters) =>
      ["seller", storeSlug, "orders", "smart", filters ?? {}] as const,
    orderById: (storeSlug: string, orderId: string) =>
      ["seller", storeSlug, "orders", orderId] as const,
    orderProof: (storeSlug: string, orderId: string) =>
      ["seller", storeSlug, "orders", orderId, "proof"] as const,
    orderPaymentTransaction: (storeSlug: string, orderId: string) =>
      ["seller", storeSlug, "orders", orderId, "payment-transaction"] as const,
    orderShipment: (storeSlug: string, orderId: string) =>
      ["seller", storeSlug, "orders", orderId, "shipment"] as const,
    orderShipmentOptions: (storeSlug: string, orderId: string) =>
      ["seller", storeSlug, "orders", orderId, "shipment", "options"] as const,
    transactionsPrefix: (storeSlug: string) =>
      ["seller", storeSlug, "transactions"] as const,
    transactions: (storeSlug: string, filters?: QueryKeyFilters) =>
      ["seller", storeSlug, "transactions", "list", filters ?? {}] as const,
    transactionById: (storeSlug: string, transactionId: string) =>
      ["seller", storeSlug, "transactions", transactionId] as const,
    paymentOptionsPrefix: (storeSlug: string) =>
      ["seller", storeSlug, "payment-options"] as const,
    paymentOptions: (storeSlug: string, filters?: QueryKeyFilters) =>
      ["seller", storeSlug, "payment-options", "list", filters ?? {}] as const,
    policyTemplates: (storeSlug: string) =>
      ["seller", storeSlug, "policy-templates"] as const,
    bostaConnection: (storeSlug: string) =>
      ["seller", storeSlug, "bosta-connection"] as const,
    jntConnection: (storeSlug: string) =>
      ["seller", storeSlug, "jnt-connection"] as const,
    paymentGatewayConnection: (storeSlug: string, provider: string) =>
      ["seller", storeSlug, "payment-gateway", provider] as const,
    reviewsPrefix: (storeSlug: string) =>
      ["seller", storeSlug, "reviews"] as const,
    reviews: (storeSlug: string, filters?: QueryKeyFilters) =>
      ["seller", storeSlug, "reviews", "list", filters ?? {}] as const,
    customersPrefix: (storeSlug: string) =>
      ["seller", storeSlug, "customers"] as const,
    customers: (storeSlug: string, filters?: QueryKeyFilters) =>
      ["seller", storeSlug, "customers", "list", filters ?? {}] as const,
    customerDetail: (storeSlug: string, customerId: string) =>
      ["seller", storeSlug, "customers", "detail", customerId] as const,
    couponsPrefix: (storeSlug: string) =>
      ["seller", storeSlug, "coupons"] as const,
    coupons: (storeSlug: string) =>
      ["seller", storeSlug, "coupons", "list"] as const,
    rolesPrefix: (storeSlug: string) => ["seller", storeSlug, "roles"] as const,
    roles: (storeSlug: string) =>
      ["seller", storeSlug, "roles", "list"] as const,
    rolePermissions: (storeSlug: string) =>
      ["seller", storeSlug, "roles", "permissions"] as const,
    membersPrefix: (storeSlug: string) =>
      ["seller", storeSlug, "members"] as const,
    members: (storeSlug: string) =>
      ["seller", storeSlug, "members", "list"] as const,
    invitesPrefix: (storeSlug: string) =>
      ["seller", storeSlug, "invites"] as const,
    invites: (storeSlug: string) =>
      ["seller", storeSlug, "invites", "list"] as const,
    search: (storeSlug: string, q: string) =>
      ["seller", storeSlug, "search", q] as const,
    dashboardAnalyticsPrefix: (storeSlug: string) =>
      ["seller", storeSlug, "dashboard-analytics"] as const,
    dashboardAnalytics: (storeSlug: string, range: string) =>
      ["seller", storeSlug, "dashboard-analytics", range] as const,
  },
} as const;
